import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import {
  PaperClipIcon,
  XMarkIcon,
  PhotoIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { uploadFileToS3 } from '../../../../../services/s3Service';
import { formatFileSize } from '../../../../../utils/formatters';
import {
  MAX_EMAIL_ATTACHMENTS_BYTES,
  mensajeDemasiadoGrande,
} from '@claimup/shared/constants/email-attachments';
import SelectorDocumentosExpediente from '../../../../../components/SelectorDocumentosExpediente';
import slugify from 'slugify';
import 'react-quill-new/dist/quill.snow.css';

const TOOLBAR_OPTIONS = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
];

const QUILL_MODULES = {
  toolbar: TOOLBAR_OPTIONS,
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'link'];

const EmailComposer = ({
  expedientId,
  onSend,
  isSending,
  replyToThreadId,
  replyToSubject,
  onCancelReply,
  disabled = false,
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Update subject when reply mode changes
  useEffect(() => {
    if (replyToSubject) {
      setSubject(`Re: ${replyToSubject.replace(/^Re:\s*/i, '')}`);
    }
  }, [replyToThreadId, replyToSubject]);
  const [attachments, setAttachments] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [verDocumentos, setVerDocumentos] = useState(false);
  const fileInputRef = useRef(null);

  const handleBodyChange = useCallback(value => {
    setBody(value);
  }, []);

  const getFileExtension = filename => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 1);
  };

  const handleFileSelect = async e => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // El tope sale de la constante compartida: aquí decía 25 MB y el servidor
    // cortaba en 15, así que se subía el fichero para que lo rechazaran después.
    const currentTotalSize = attachments.reduce((acc, att) => acc + (att.size || 0), 0);
    const newFilesSize = files.reduce((acc, file) => acc + file.size, 0);
    const total = currentTotalSize + newFilesSize;

    if (total > MAX_EMAIL_ATTACHMENTS_BYTES) {
      toast.error(mensajeDemasiadoGrande(total));
      return;
    }

    setIsUploadingFile(true);

    try {
      const uploadedFiles = await Promise.all(
        files.map(async file => {
          const extension = '.' + getFileExtension(file.name);
          const filenameWithoutExt =
            file.name.slice(0, file.name.lastIndexOf('.')) || file.name;
          const timestamp = Date.now();
          const slug = `email-${timestamp}-${slugify(filenameWithoutExt, {
            lower: true,
            strict: true,
          })}${extension}`;
          const slugWithFolder = `email/attachments/${slug}`;

          const uploadResult = await uploadFileToS3(
            file,
            expedientId,
            slugWithFolder,
            file.name,
            file.type
          );

          return {
            filename: file.name,
            mime_type: file.type,
            size: file.size,
            s3_key: uploadResult.s3Key,
            s3_url: uploadResult.s3Url,
          };
        })
      );

      setAttachments(prev => [...prev, ...uploadedFiles]);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Error al subir los archivos');
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Los documentos elegidos del expediente entran en la MISMA lista que lo
   * subido del ordenador: por debajo nadie distingue de dónde vino.
   *
   * No se cuela dos veces el mismo: adjuntar el duplicado no da error en
   * ningún sitio y quien lo nota es el deudor.
   */
  const anadirDelExpediente = elegidos => {
    setAttachments(prev => {
      const puestos = new Set(prev.map(a => a.s3_key).filter(Boolean));
      return [...prev, ...elegidos.filter(d => !puestos.has(d.s3_key))];
    });
  };

  const removeAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!subject.trim()) {
      toast.error('El asunto es obligatorio');
      return;
    }

    // Check if body has content (strip HTML tags and check)
    const plainTextBody = body.replace(/<[^>]*>/g, '').trim();
    if (!plainTextBody) {
      toast.error('El mensaje es obligatorio');
      return;
    }

    const sanitizedBody = DOMPurify.sanitize(body);

    await onSend({
      subject: subject.trim(),
      body: sanitizedBody,
      reply_to_thread_id: replyToThreadId || undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
    });

    // Reset form on success
    setSubject('');
    setBody('');
    setAttachments([]);
  };

  const isReplyMode = !!replyToThreadId;

  return (
    <div className='bg-white border-t border-gray-200 p-3 flex-none'>
      {/* Reply indicator */}
      {isReplyMode && (
        <div className='flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2 mb-2'>
          <span className='text-sm text-blue-700'>
            Respondiendo a: <span className='font-medium'>{replyToSubject}</span>
          </span>
          <button
            type='button'
            onClick={onCancelReply}
            className='text-blue-600 hover:text-blue-800'
          >
            <XMarkIcon className='w-4 h-4' />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Subject */}
        <div className='mb-2'>
          <input
            type='text'
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder='Asunto'
            disabled={isReplyMode}
            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50'
          />
        </div>

        {/* Rich text body */}
        <div className='mb-2 [&_.ql-container]:rounded-b-lg [&_.ql-toolbar]:rounded-t-lg [&_.ql-editor]:min-h-[60px] [&_.ql-editor]:max-h-[120px] [&_.ql-editor]:overflow-y-auto'>
          <ReactQuill
            theme='snow'
            value={body}
            onChange={handleBodyChange}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder='Escribe tu mensaje...'
          />
        </div>

        {/* Attachments preview */}
        {attachments.length > 0 && (
          <div className='mb-2 space-y-1'>
            {attachments.map((attachment, index) => (
              <div
                key={`${attachment.filename}-${index}`}
                className='flex items-center gap-2 p-2 bg-gray-50 rounded-lg'
              >
                {attachment.mime_type?.startsWith('image/') ? (
                  <PhotoIcon className='w-4 h-4 text-gray-500 flex-shrink-0' />
                ) : (
                  <PaperClipIcon className='w-4 h-4 text-gray-500 flex-shrink-0' />
                )}
                <span className='text-sm text-gray-700 truncate flex-1'>
                  {attachment.filename}
                </span>
                <span className='text-xs text-gray-500'>
                  {formatFileSize(attachment.size)}
                </span>
                <button
                  type='button'
                  onClick={() => removeAttachment(index)}
                  className='text-red-500 hover:text-red-700'
                >
                  <XMarkIcon className='w-4 h-4' />
                </button>
              </div>
            ))}
          </div>
        )}

        {verDocumentos && (
          <SelectorDocumentosExpediente
            expedientId={expedientId}
            yaAdjuntos={attachments}
            onElegir={anadirDelExpediente}
            onCerrar={() => setVerDocumentos(false)}
          />
        )}

        {/* Actions */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            {/* File input */}
            <input
              ref={fileInputRef}
              type='file'
              id='email-file-input'
              className='hidden'
              onChange={handleFileSelect}
              multiple
              accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv'
            />
            <label
              htmlFor='email-file-input'
              className='p-2 text-gray-600 hover:text-blue-600 cursor-pointer'
              title='Adjuntar archivo'
            >
              <PaperClipIcon className='w-5 h-5' />
            </label>
            <button
              type='button'
              onClick={() => setVerDocumentos(v => !v)}
              disabled={disabled}
              className='inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-50'
            >
              <DocumentTextIcon className='w-4 h-4' />
              Del expediente
            </button>
          </div>

          <button
            type='submit'
            disabled={isSending || isUploadingFile || disabled}
            className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
              isSending || isUploadingFile || disabled
                ? 'bg-blue-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isUploadingFile
              ? 'Subiendo...'
              : isSending
              ? 'Enviando...'
              : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailComposer;
